import { useCallback, useEffect, useState } from "react";
import StatusBadge from "../intelligence/StatusBadge";
import {
  getAdminCanonicalPropertyMedia,
  patchAdminCanonicalGalleryImage,
  recalculateAdminCanonicalPropertyMedia,
} from "../../../services/midpointApi";
import { resolvePublicImageUrl } from "../../../utils/resolvePublicImageUrl";

function mediaTone(row) {
  if (row.isHidden) {
    return "neutral";
  }

  if (row.duplicateStatus === "DUPLICATE_SUPPRESSED") {
    return "warning";
  }

  if (row.healthStatus === "BROKEN" || row.healthStatus === "INVALID_URL") {
    return "danger";
  }

  if (row.cacheStatus === "CACHED") {
    return "success";
  }

  return "medium";
}

const CanonicalPropertyMediaSection = ({ canonicalPropertyId }) => {
  const [media, setMedia] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const loadMedia = useCallback(async () => {
    if (!canonicalPropertyId) {
      return;
    }

    try {
      setLoading(true);
      setError("");
      const response = await getAdminCanonicalPropertyMedia(canonicalPropertyId);
      setMedia(response);
    } catch (err) {
      setMedia(null);
      setError(err.message || "Unable to load media.");
    } finally {
      setLoading(false);
    }
  }, [canonicalPropertyId]);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  const runAction = async (galleryImageId, action, extra = {}) => {
    try {
      setBusy(true);
      await patchAdminCanonicalGalleryImage(galleryImageId, { action, ...extra });
      await loadMedia();
    } catch (err) {
      setError(err.message || "Media action failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleRecalculate = async () => {
    try {
      setBusy(true);
      await recalculateAdminCanonicalPropertyMedia(canonicalPropertyId);
      await loadMedia();
    } catch (err) {
      setError(err.message || "Gallery recalculation failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleClearPrimary = async () => {
    try {
      setBusy(true);
      await recalculateAdminCanonicalPropertyMedia(canonicalPropertyId, {
        action: "CLEAR_PRIMARY_OVERRIDE",
      });
      await loadMedia();
    } catch (err) {
      setError(err.message || "Unable to clear primary override.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <p className="intel-meta">Loading media…</p>;
  }

  if (error) {
    return <p className="admin-error">{error}</p>;
  }

  if (!media) {
    return null;
  }

  const renderGrid = (title, items = []) => {
    if (!items.length) {
      return null;
    }

    return (
      <>
        <h4>{title}</h4>
        <div className="admin-canonical-media__grid">
          {items.map((row) => {
            const preview = resolvePublicImageUrl(row.displayImageUrl || row.cachedImageUrl || row.imageUrl);

            return (
              <article key={row.galleryImageId} className="admin-canonical-media__card">
                {preview ? <img src={preview} alt="" loading="lazy" /> : <div className="property-image-placeholder" />}
                <div className="admin-canonical-media__card-body">
                  <StatusBadge label={row.duplicateStatus || row.cacheStatus || "—"} tone={mediaTone(row)} />
                  <StatusBadge label={row.healthStatus || "—"} tone={mediaTone(row)} />
                  {row.isPrimary ? <StatusBadge label="Primary" tone="success" /> : null}
                  {row.isHidden ? <StatusBadge label="Hidden" tone="neutral" /> : null}
                  <p>{row.attribution || row.sourceKey}</p>
                  <p className="muted">Failures: {row.browserFailureCount ?? 0}</p>
                  <div className="admin-canonical-media__actions">
                    {!row.isPrimary ? (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => runAction(row.galleryImageId, "SET_PRIMARY")}
                      >
                        Set primary
                      </button>
                    ) : null}
                    {!row.isHidden ? (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          runAction(row.galleryImageId, "HIDE", {
                            reason: "admin_hidden",
                          })
                        }
                      >
                        Hide
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => runAction(row.galleryImageId, "UNHIDE")}
                      >
                        Unhide
                      </button>
                    )}
                    {row.imageUrl ? (
                      <a href={row.imageUrl} target="_blank" rel="noreferrer">
                        Original
                      </a>
                    ) : null}
                    {row.cachedImageUrl ? (
                      <a href={resolvePublicImageUrl(row.cachedImageUrl)} target="_blank" rel="noreferrer">
                        Cached
                      </a>
                    ) : null}
                    {row.sourcePageUrl ? (
                      <a href={row.sourcePageUrl} target="_blank" rel="noreferrer">
                        Listing
                      </a>
                    ) : null}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <section className="admin-canonical-media">
      <div className="section-heading">
        <h3>Media</h3>
        <span className="status-pill">{media.metrics?.visibleImageCount ?? 0} visible</span>
      </div>
      <p className="intel-meta">
        {media.metrics?.imageCount ?? 0} tracked · {media.metrics?.hiddenImageCount ?? 0} hidden
        {media.manualPrimaryGalleryImageId ? " · manual primary set" : ""}
      </p>
      <div className="admin-canonical-detail__actions">
        <button type="button" className="ghost-button" disabled={busy} onClick={handleRecalculate}>
          Recalculate gallery
        </button>
        {media.manualPrimaryGalleryImageId ? (
          <button type="button" className="ghost-button" disabled={busy} onClick={handleClearPrimary}>
            Clear primary override
          </button>
        ) : null}
      </div>
      {renderGrid("Visible gallery", media.visible)}
      {renderGrid("Hidden", media.hidden)}
      {renderGrid("Duplicate suppressed", media.duplicateSuppressed)}
      {renderGrid("Broken / excluded", media.broken)}
    </section>
  );
};

export default CanonicalPropertyMediaSection;
